import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill, EnvBadge } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FolderKanban, Loader2, Plus, ChevronRight, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");
  const [portfolioRegistry, setPortfolioRegistry] = useState(null);
  const [workspaceSources, setWorkspaceSources] = useState(null);
  const [workspaceSourcesError, setWorkspaceSourcesError] = useState(false);
  const [operatingContexts, setOperatingContexts] = useState({ modules: null, playbooks: null });
  const [contextsError, setContextsError] = useState(false);
  const [showContexts, setShowContexts] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", industry: "", objective: "" });
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const load = () => api.projects().then((rows) => { setProjects(rows); setLoadError(""); }).catch(() => { setProjects([]); setLoadError("Tidak dapat memuat data portofolio dari server. Coba lagi."); });
  useEffect(() => {
    load();
    api.portfolioRegistry().then(setPortfolioRegistry).catch(() => setPortfolioRegistry(null));
    api.workspaceSources().then((data) => { setWorkspaceSources(data); setWorkspaceSourcesError(false); }).catch(() => { setWorkspaceSources(null); setWorkspaceSourcesError(true); });
    Promise.all([api.operatingModules("ACTIVE"), api.executionPlaybooks("ACTIVE")])
      .then(([modules, playbooks]) => {
        setOperatingContexts({ modules: Array.isArray(modules) ? modules : [], playbooks: Array.isArray(playbooks) ? playbooks : [] });
        setContextsError(false);
      })
      .catch(() => { setOperatingContexts({ modules: [], playbooks: [] }); setContextsError(true); });
  }, []);

  const create = async () => {
    if (!form.code || !form.name) return toast.error(t("projects.codeNameRequired"));
    try {
      await api.createProject(form);
      toast.success(t("projects.created", { code: form.code }));
      setOpen(false); setForm({ code: "", name: "", industry: "", objective: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.failed")); }
  };

  if (!projects) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;
  const filteredProjects = projects.filter((project) => [project.name, project.code, project.industry, project.current_focus, project.project_role]
    .some((value) => String(value || "").toLocaleLowerCase("id-ID").includes(search.trim().toLocaleLowerCase("id-ID"))));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="mono-label">FOUNDER PORTFOLIO</p>
          <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><FolderKanban className="text-[#2563EB]" /> Portofolio Bisnis</h1>
          <p className="text-sm text-[#94A3B8] mt-1">{lang === "id" ? "Project aktif dan workspace operasional perusahaan." : "Active company projects and operational workspaces."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap"><button onClick={() => navigate("/continuity")} className="btn-primary h-10 px-4 text-sm flex items-center gap-2" data-testid="portfolio-continuity-link">Lanjutkan dari ChatGPT <ArrowRight size={14} /></button><button onClick={() => navigate("/businesses")} className="btn-ghost h-10 px-4 text-sm" data-testid="portfolio-business-operations">Operasional Bisnis →</button><Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button className="btn-primary h-10 px-4 text-sm flex items-center gap-2" data-testid="create-project-button"><Plus size={16} /> {t("projects.new")}</button></DialogTrigger>
          <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0]">
            <DialogHeader><DialogTitle className="font-display text-white">{t("projects.createTitle")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["code", t("projects.phCode")], ["name", t("projects.phName")], ["industry", t("projects.phIndustry")], ["objective", t("projects.phObjective")]].map(([k, ph]) => (
                <input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="input-dark w-full h-10 px-3 text-sm" data-testid={`project-input-${k}`} />
              ))}
              <button onClick={create} className="btn-primary w-full h-10 text-sm" data-testid="project-create-submit">{t("projects.createSubmit")}</button>
            </div>
          </DialogContent>
        </Dialog></div>
      </div>

      <section className="m-card p-4 space-y-3" data-testid="founder-portfolio-inventory">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div><h2 className="text-white font-semibold">Project terdaftar di MASANDI OS</h2>
            <p className="text-xs text-[#94A3B8] mt-1">Menampilkan data dari database MASANDI OS. ChatGPT Projects dan project bisnis adalah dua jenis entitas berbeda; daftar ini bukan bukti bahwa seluruh workspace ChatGPT sudah terhubung.</p>
          </div>
          <span className="m-pill">{loadError ? "DATA TIDAK TERSEDIA" : `${projects.length} terdaftar`}</span>
        </div>
        {loadError && <p role="alert" className="text-[#FB7185] text-xs">{loadError} <button onClick={load} className="underline ml-2">Coba lagi</button></p>}
        <label className="block">
          <span className="text-xs text-[#94A3B8]">Cari bisnis, nama project, kode, atau fokus</span>
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)}
            className="input-dark h-10 px-3 w-full mt-1 text-sm" placeholder="Contoh: GEMA ERP, LURI SALA..."
            data-testid="portfolio-search" />
        </label>
      </section>

      <section className="m-card p-4" data-testid="masandi-operating-contexts">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-white font-semibold">Peta fungsi MASANDI</h2>
            <p className="text-xs text-[#94A3B8] mt-1">
              Mapping dari registry MASANDI OS: operating modules mengatur tanggung jawab,
              execution playbooks mengatur cara kerja. Ini bukan daftar project bisnis ataupun
              bukti sinkronisasi otomatis percakapan ChatGPT.
            </p>
          </div>
          <button onClick={() => setShowContexts((value) => !value)} className="btn-ghost h-9 px-3 text-xs"
            data-testid="operating-context-toggle">
            {showContexts ? "Sembunyikan" : "Lihat peta"} · {operatingContexts.modules && operatingContexts.playbooks
              ? operatingContexts.modules.length + operatingContexts.playbooks.length : "…"}
          </button>
        </div>
        {contextsError && <p role="alert" className="text-[#FB7185] text-xs mt-3">
          Registry fungsi belum dapat dimuat. Cek koneksi backend sebelum mengambil keputusan.
        </p>}
        {showContexts && !contextsError && (
          <div className="grid lg:grid-cols-2 gap-3 mt-4">
            {[
              ["Operating Modules", operatingContexts.modules, "Fungsi / divisi"],
              ["Execution Playbooks", operatingContexts.playbooks, "Metode eksekusi"],
            ].map(([heading, items, type]) => (
              <div key={heading} className="rounded-lg border border-[#274268] p-3">
                <div className="flex justify-between gap-2">
                  <h3 className="text-white text-sm font-semibold">{heading}</h3>
                  <span className="m-pill">{items?.length ?? "…"} tercatat</span>
                </div>
                {items === null ? <p className="text-[#94A3B8] text-xs mt-2">Memuat registry…</p> :
                  items.length === 0 ? <p className="text-[#94A3B8] text-xs mt-2">Tidak ada konteks yang terdaftar.</p> :
                  <div className="divide-y divide-[#1E293B] mt-2">
                    {items.map((item) => (
                      <div key={item.code} className="py-2" data-testid={`operating-context-${item.code}`}>
                        <div className="flex justify-between gap-2">
                          <span className="text-[#E2E8F0] text-xs font-medium">{item.name}</span>
                          <span className="text-[#94A3B8] text-[10px] shrink-0">TERDEFINISI</span>
                        </div>
                        <p className="text-[10px] text-[#64748B] mt-0.5">{type} · {item.code}</p>
                        <p className="text-[10px] text-[#94A3B8] mt-1">
                          Konteks historis: {(item.source_context || []).join(" · ") || "Belum terdokumentasi"}
                        </p>
                      </div>
                    ))}
                  </div>}
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-[#FBBF24] mt-3">
          Catatan: 11 modul + 5 playbook = 16 konteks internal. Jumlah workspace ChatGPT yang disebut Founder adalah 15;
          identitas dan izin akses tiap workspace belum terverifikasi. Status: terpetakan di OS, bukan tersinkronisasi.
        </p>
      </section>

      <section className="m-card p-4" data-testid="founder-chatgpt-workspaces">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h2 className="text-white font-semibold">Workspace ChatGPT — identitas terverifikasi</h2>
            <p className="text-xs text-[#94A3B8] mt-1">
              Nama dan ID diambil dari daftar project akun Founder pada 21 Oktober 2026.
              Ini penghubung identitas untuk navigasi, bukan sinkronisasi isi percakapan.
            </p>
          </div>
          <span className="m-pill text-[#FBBF24]">
            {workspaceSources ? `${workspaceSources.chatgpt_workspace_count} TERPETAKAN` :
              workspaceSourcesError ? "TIDAK TERSEDIA" : "MEMUAT…"}
          </span>
        </div>
        {workspaceSourcesError ? <p className="text-[#FB7185] text-xs mt-3" role="alert">
          Data identitas workspace belum dapat dimuat. Tidak menampilkan data palsu.
        </p> : null}
        {workspaceSources && (
          <>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <span className="m-pill">{workspaceSources.operating_module_count} modul</span>
              <span className="m-pill">{workspaceSources.execution_playbook_count} playbook</span>
              <span className="m-pill text-[#FBBF24]">CHAT SYNC: BELUM TERHUBUNG</span>
            </div>
            <div className="grid md:grid-cols-2 gap-2 mt-4">
              {workspaceSources.items.map((entry) => (
                <div key={entry.workspace_project_id} className="rounded-lg border border-[#274268] p-3"
                  data-testid={`chatgpt-workspace-${entry.operating_code}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-white text-xs font-semibold break-words">{entry.workspace_name}</p>
                      <p className="text-[#94A3B8] text-[11px] mt-1">{entry.operating_name} · {entry.operating_code}</p>
                    </div>
                    <a href={entry.workspace_url} target="_blank" rel="noopener noreferrer"
                      className="text-[#60A5FA] text-xs shrink-0" aria-label={`Buka ChatGPT project ${entry.workspace_name}`}>Buka ↗</a>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#FBBF24] mt-3">
              Sumber: sidebar ChatGPT Founder (snapshot {workspaceSources.snapshot_date}).
              Project ChatGPT tetap dikelola oleh ChatGPT; perubahan percakapan, file,
              dan keputusan belum otomatis masuk MASANDI OS.
            </p>
          </>
        )}
      </section>

      {portfolioRegistry?.reserved_codes?.P002 && (
        <div className="m-card p-3 text-xs text-[#94A3B8]" data-testid="portfolio-p002-reserved">
          <span className="font-mono text-[#FCD34D]">P002 RESERVED</span>
          {" · "}
          {lang === "id"
            ? "kode historis test/QA terisolasi; sengaja tidak dipakai ulang untuk project production."
            : "historical isolated test/QA code; intentionally not reused for a production project."}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map((p) => (
          <div key={p.code} className="m-card m-card-hover p-5 cursor-pointer" onClick={() => navigate(`/projects/${p.code}`)} data-testid={`project-card-${p.code}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2"><span className="mono-label">{p.code}</span>{p.priority_class && <span className="m-pill text-[10px]">{p.priority_class}</span>}</div>
              <div className="flex items-center gap-1.5"><EnvBadge environment={p.environment} archived={p.archived} /><StatusPill status="ACTIVE" label={p.stage} /></div>
            </div>
            <h2 className="font-display text-xl font-bold text-white mt-3">{p.name}</h2>
            <p className="text-xs text-[#94A3B8] mt-1">{p.industry}</p>
            {p.project_role && <p className="text-[10px] text-[#64748B] font-mono mt-2">{p.project_role.replace(/_/g, " ")}</p>}
            <p className="text-sm text-[#E2E8F0] mt-3 line-clamp-2">{p.current_focus || p.primary_objective}</p>
            {p.next_milestone && <p className="text-xs text-[#64748B] mt-3 line-clamp-2"><span className="text-[#94A3B8]">{lang === "id" ? "Berikutnya:" : "Next:"}</span> {p.next_milestone}</p>}
            {p.canonical_repo && <p className="text-[10px] text-[#475569] font-mono mt-3">{p.canonical_repo}</p>}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1E293B]"><span className="text-xs text-[#64748B]">{t("projects.health", { v: p.health?.replace(/_/g," ") })}</span><ChevronRight size={16} className="text-[#64748B]" /></div>
          </div>
        ))}
      </div>
      {filteredProjects.length === 0 && !loadError && <div className="m-card p-5 text-center text-sm text-[#94A3B8]">
        {search ? "Tidak ada project terdaftar yang cocok. Periksa nama atau kode." : "Belum ada project produksi yang tercatat."}
      </div>}
    </div>
  );
}
